import { useMemo } from "react"
import { Box, Stack } from "@mui/material"
import dayjs from "dayjs"
import { Heading, ViraCard, ViraText } from "@ksana/vira-ui"
import {
  ClientFragment,
  useNudgesScheduleByUserQuery,
  useQuestionnairesScheduleByUserQuery,
} from "../../generated/graphql"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { isNudgeSchedule } from "../../utils/helpers"
import {
  getItemDataFromScheduleItem,
  takeCompletionOnDate,
} from "../../utils/scheduleUtils"
import LoopMoodTrends from "./LoopMoodTrends"

type PrepareSignalsProps = {
  client: ClientFragment
}

/**
 * Compact mood + plan-adherence context for Loop Prepare (deck slides 3–4),
 * reusing Overview schedule takes and Summary mood charts.
 */
export default function PrepareSignals({ client }: PrepareSignalsProps) {
  const graphqlClient = useGraphQLClient()
  const { data: nudgesData, isLoading: loadingNudges } =
    useNudgesScheduleByUserQuery(graphqlClient, {
      user_id: client.id,
    })
  const { data: questionnairesData, isLoading: loadingQuestionnaires } =
    useQuestionnairesScheduleByUserQuery(graphqlClient, {
      user_id: client.id,
    })

  const adherence = useMemo(() => {
    const items = [
      ...(nudgesData?.nudge_schedules ?? []),
      ...(questionnairesData?.questionnaire_schedules ?? []),
    ]
    const start = dayjs().startOf("day").subtract(6, "day")
    const end = dayjs().endOf("day")
    let scheduled = 0
    let completed = 0

    for (const item of items) {
      if (!item.one_time || !item.scheduled_at) {
        continue
      }
      const when = dayjs(item.scheduled_at)
      if (when.isBefore(start) || when.isAfter(end)) {
        continue
      }
      scheduled += 1
      const { takes } = getItemDataFromScheduleItem(item)
      const dateKey = when.format("YYYY-MM-DD")
      if (takeCompletionOnDate(dateKey, takes ?? [], isNudgeSchedule(item))) {
        completed += 1
      }
    }

    return { scheduled, completed }
  }, [nudgesData, questionnairesData])

  const loading = loadingNudges || loadingQuestionnaires
  const rateLabel =
    adherence.scheduled === 0
      ? "No one-time plan items in the last 7 days"
      : `${adherence.completed} of ${adherence.scheduled} completed this week`

  return (
    <Stack spacing={2}>
      <ViraCard padded>
        <Heading level="card" sx={{ mb: 0.75, fontSize: 17, fontWeight: 700 }}>
          Plan adherence
        </Heading>
        <ViraText tone="muted" sx={{ fontSize: 14, lineHeight: 1.5, mb: 1.25 }}>
          One-time nudges and assessments scheduled in the last 7 days
        </ViraText>
        <ViraText
          sx={{
            fontSize: 15.5,
            fontWeight: 700,
            lineHeight: 1.4,
          }}
        >
          {loading ? "Loading schedule…" : rateLabel}
        </ViraText>
      </ViraCard>

      <ViraCard padded>
        <Heading level="card" sx={{ mb: 1.25, fontSize: 17, fontWeight: 700 }}>
          Mood this week
        </Heading>
        <Box sx={{ width: "100%", minWidth: 0 }}>
          <LoopMoodTrends data={client.mood_measurements} />
        </Box>
      </ViraCard>
    </Stack>
  )
}
